import React, { useState, useRef, useCallback, useEffect } from 'react';
import { detectMediaSource } from '../../utils/mediaSource';
import { usePreferences } from '../../context/UserPreferencesContext';
import YouTubePlayer from './YouTubePlayer';
import Tooltip from '../Tooltip';
import Icon from '../Icon';
// Reuse the existing VideoPlayer styles (.video-player* class names) to avoid churn.
import '../annotate/VideoPlayer.css';
import './MediaPlayer.css';

/**
 * MediaPlayer — pluggable media wrapper (Requirement 18).
 *
 * Owns the shared responsibilities that used to live in VideoPlayer: URL input,
 * source detection, title card, collapse toggle, control bar, playback-time
 * tracking, the window bridges, and the clear/confirm flow. It delegates the
 * actual media rendering to a source-specific sub-player (currently only
 * YouTubePlayer). New source types can be added without touching consumer pages.
 *
 * Props (same shape as the old VideoPlayer so pages barely change):
 *   defaultVideoUrl (string) — a video or playlist URL
 *   placeholder (string) — hint text in the empty link field
 *   onVideoLoad(id, title) — called once per video; in a playlist, again each
 *                            time the player moves to another video
 *   onTimeUpdate(time)
 *   onVideoClear()
 *   titleContent (node) — optional: shown under the title card while the
 *                         video is expanded (the title's meaning and words)
 */
// YouTube player states (YT.PlayerState).
const YT_PLAYING = 1;
const YT_BUFFERING = 3;
const isActive = (state) => state === YT_PLAYING || state === YT_BUFFERING;
const playerStateOf = (player) => {
  try {
    const state = player && player.getPlayerState && player.getPlayerState();
    return typeof state === 'number' ? state : null;
  } catch (e) {
    return null;
  }
};
// How long after a tap the button is checked against the player.
const CONFIRM_MS = 1500;
// How long after showAt a video starting to play is paused again.
const HOLD_PAUSED_MS = 2000;

function MediaPlayer({ onVideoLoad, onTimeUpdate, onVideoClear, defaultVideoUrl, placeholder = 'Paste a media link (YouTube)', titleContent }) {
  const { targetLanguage } = usePreferences();

  const defaultSource = defaultVideoUrl
    ? detectMediaSource(defaultVideoUrl)
    : { type: 'unknown', id: null };

  const [url, setUrl] = useState(defaultVideoUrl || '');
  const [mediaSource, setMediaSource] = useState(defaultSource);
  const [title, setTitle] = useState('');
  // The video starts collapsed; it plays (audio, controls) all the same.
  const [collapsed, setCollapsed] = useState(true);
  // "…": the playlist's previous / next video, hidden until asked for.
  const [showMore, setShowMore] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');

  const playerRef = useRef(null);
  // Until when a video that starts playing is paused again (showAt).
  const holdPausedUntilRef = useRef(0);
  // The video the parent was last told about. Notify only on change, so
  // re-renders don't clear the parent's subtitles, but a playlist moving on
  // to its next video does reach the parent.
  const notifiedVideoIdRef = useRef(null);
  const isPlaylist = !!mediaSource.playlistId;
  const hasMedia = mediaSource.type === 'youtube' && !!(mediaSource.id || mediaSource.playlistId);
  const intervalRef = useRef(null);

  const handleSubmit = () => {
    setError('');
    const detected = detectMediaSource(url.trim());
    if (detected.type === 'youtube' && (detected.id || detected.playlistId)) {
      setMediaSource(detected);
      notifiedVideoIdRef.current = null;
      return;
    }
    if (detected.type === 'spotify') {
      setError('Spotify is not supported yet.');
      return;
    }
    if (detected.type === 'instagram') {
      setError('Instagram is not supported yet.');
      return;
    }
    setError('Please enter a valid YouTube URL');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  // Pick up the player's current video: its title, and tell the parent when
  // it is a different video from the one it last heard about.
  const syncCurrentVideo = (player) => {
    let data;
    try { data = player.getVideoData() || {}; } catch (e) { return; }
    const playerVideoId = data.video_id;
    if (!playerVideoId) return;
    const videoTitle = data.title || 'Untitled video';
    setTitle(videoTitle);
    if (notifiedVideoIdRef.current !== playerVideoId) {
      notifiedVideoIdRef.current = playerVideoId;
      onVideoLoad && onVideoLoad(playerVideoId, videoTitle);
    }
  };

  const handleReady = (event) => {
    playerRef.current = event.target;
    syncCurrentVideo(event.target);
    startTimeTracking();
  };

  const handleStateChange = (event) => {
    if (isPlaylist) syncCurrentVideo(event.target);
    // Shown at a moment (showAt): a video that starts playing from the seek
    // (YouTube plays one that hadn't started) is paused there again.
    if (isActive(event.data) && Date.now() < holdPausedUntilRef.current) {
      try { event.target.pauseVideo(); } catch (e) { /* player gone */ }
      setPlaying(false);
      return;
    }
    // Buffering after a play counts as playing: the button shows Pause
    // while the video loads, and a tap then pauses it.
    setPlaying(isActive(event.data));
    if (event.data === YT_PLAYING) {
      startTimeTracking();
    } else if (event.data !== YT_BUFFERING) {
      stopTimeTracking();
      updateCurrentTime();
    }
  };

  const startTimeTracking = () => {
    stopTimeTracking();
    intervalRef.current = setInterval(updateCurrentTime, 500);
  };

  const stopTimeTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // The latest onTimeUpdate, for callbacks created once (seekTo).
  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;

  const reportTime = (time) => {
    setCurrentTime(time);
    onTimeUpdateRef.current && onTimeUpdateRef.current(time);
  };

  // Time is only polled while playing, so a seek reports its target at once
  // (paused, nothing else would), then the player's own time once it lands.
  const afterSeek = (target) => {
    let t = Math.max(0, target);
    try {
      const d = playerRef.current.getDuration && playerRef.current.getDuration();
      if (d > 0) t = Math.min(t, d);
    } catch (e) { /* keep the target */ }
    reportTime(t);
    setTimeout(() => { if (playerRef.current) updateCurrentTimeRef.current(); }, 400);
  };

  const updateCurrentTime = () => {
    if (playerRef.current) {
      try {
        reportTime(playerRef.current.getCurrentTime());
      } catch (e) {
        // Player may be destroyed — stop tracking
        stopTimeTracking();
      }
    }
  };

  const afterSeekRef = useRef(afterSeek);
  afterSeekRef.current = afterSeek;
  const updateCurrentTimeRef = useRef(updateCurrentTime);
  updateCurrentTimeRef.current = updateCurrentTime;

  const handleClose = () => setShowConfirm(true);

  const confirmClear = () => {
    stopTimeTracking();
    setMediaSource({ type: 'unknown', id: null });
    setTitle('');
    setUrl('');
    setCollapsed(false);
    setPlaying(false);
    setCurrentTime(0);
    setShowConfirm(false);
    notifiedVideoIdRef.current = null;
    playerRef.current = null;
    onVideoClear && onVideoClear();
  };

  const cancelClear = () => setShowConfirm(false);

  // The button flips as soon as it is tapped, not when YouTube reports back
  // (after buffering, often a few hundred ms); its state events then
  // confirm it, and a check shortly after puts it right if the player
  // didn't follow (e.g. a browser blocking playback).
  const confirmTimerRef = useRef(null);
  const showIntent = (willPlay) => {
    setPlaying(willPlay);
    clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = setTimeout(() => {
      const state = playerStateOf(playerRef.current);
      if (state !== null) setPlaying(isActive(state));
    }, CONFIRM_MS);
  };
  useEffect(() => () => clearTimeout(confirmTimerRef.current), []);

  const showIntentRef = useRef(showIntent);
  showIntentRef.current = showIntent;

  const togglePlay = () => {
    if (!playerRef.current) return;
    holdPausedUntilRef.current = 0;
    // Asked of the player now, not taken from the last state event.
    const state = playerStateOf(playerRef.current);
    const active = state === null ? playing : isActive(state);
    try {
      if (active) playerRef.current.pauseVideo();
      else playerRef.current.playVideo();
      showIntent(!active);
    } catch (e) {
      // Silently ignore — player may be destroyed (collapsed video)
    }
  };

  const seekBy = (seconds) => {
    if (!playerRef.current) return;
    try {
      const newTime = Math.max(0, playerRef.current.getCurrentTime() + seconds);
      playerRef.current.seekTo(newTime, true);
      afterSeek(newTime);
    } catch (e) {
      // Silently ignore — player may be destroyed (collapsed video)
    }
  };

  // previousVideo / nextVideo within a playlist
  const playlistStep = (method) => {
    if (!playerRef.current) return;
    try {
      playerRef.current[method]();
    } catch (e) {
      // Silently ignore — player may be destroyed (collapsed video)
    }
  };

  const seekTo = useCallback((time) => {
    if (!playerRef.current) return;
    try {
      playerRef.current.seekTo(time, true);
      afterSeekRef.current(time);
    } catch (e) {
      // Silently ignore — player may be destroyed (collapsed video)
    }
  }, []);

  const pauseVideo = useCallback(() => {
    if (!playerRef.current) return;
    try {
      playerRef.current.pauseVideo();
      showIntentRef.current(false);
    } catch (e) {
      // Silently ignore — player may be destroyed (collapsed video)
    }
  }, []);

  const playVideo = useCallback(() => {
    if (!playerRef.current) return;
    holdPausedUntilRef.current = 0;
    try {
      playerRef.current.playVideo();
      showIntentRef.current(true);
    } catch (e) {
      // Silently ignore — player may be destroyed (collapsed video)
    }
  }, []);

  // Go to a moment and stay paused there: the annotate page's lists, so the
  // teacher sees the frame of the subtitle or word they picked.
  const showAt = useCallback((time) => {
    if (!playerRef.current) return;
    holdPausedUntilRef.current = Date.now() + HOLD_PAUSED_MS;
    try {
      playerRef.current.pauseVideo();
      showIntentRef.current(false);
    } catch (e) {
      // Silently ignore — player may be destroyed (collapsed video)
    }
    seekTo(time);
  }, [seekTo]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    window.__hojaVideoSeekTo = seekTo;
    window.__hojaVideoPause = pauseVideo;
    window.__hojaVideoPlay = playVideo;
    window.__hojaVideoShowAt = showAt;
    return () => {
      delete window.__hojaVideoShowAt;
      delete window.__hojaVideoSeekTo;
      delete window.__hojaVideoPause;
      delete window.__hojaVideoPlay;
    };
  }, [seekTo, pauseVideo, playVideo, showAt]);

  // Input state — no active media yet.
  if (!hasMedia) {
    // A recognised-but-unsupported source that was set as the active media
    // renders the "not supported" block; otherwise show the URL input.
    if (mediaSource.type === 'spotify' || mediaSource.type === 'instagram') {
      return (
        <div className="video-player">
          <div className="media-player-unsupported">
            This media type is not supported yet.
          </div>
        </div>
      );
    }

    return (
      <div className="video-player">
        <div className="video-player-input-row">
          <input
            className="video-player-input"
            type="text"
            placeholder={placeholder}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Media link"
          />
          <button className="video-player-view-btn" onClick={handleSubmit}>View</button>
        </div>
        {error && <p className="video-player-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="video-player">
      <div className="video-player-title-card">
        <span className="video-player-title">{title || 'Loading…'}</span>
        <div className="video-player-title-actions">
          <Tooltip label={collapsed ? 'Show video' : 'Hide video'}>
            <button className="icon-btn icon-btn--sm" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? 'Expand video' : 'Collapse video'}>
              <Icon name={collapsed ? 'chevron-down' : 'chevron-up'} />
            </button>
          </Tooltip>
          <Tooltip label="Close video">
            <button className="icon-btn icon-btn--sm icon-btn--danger" onClick={handleClose} aria-label="Close video">
              <Icon name="x" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* The title's meaning and words: shown with the video, by the same
          expand button (Requirement 45) */}
      {!collapsed && titleContent && <div className="video-player-title-content">{titleContent}</div>}

      {showConfirm && (
        <div className="video-player-confirm">
          <p>Are you sure? This will clear the current video and all annotations shown.</p>
          <div className="video-player-confirm-actions">
            <button className="video-player-confirm-yes" onClick={confirmClear}>Confirm</button>
            <button className="video-player-confirm-no" onClick={cancelClear}>Cancel</button>
          </div>
        </div>
      )}

      <YouTubePlayer
        videoId={mediaSource.id}
        playlistId={mediaSource.playlistId}
        collapsed={collapsed}
        targetLanguage={targetLanguage}
        onReady={handleReady}
        onStateChange={handleStateChange}
        onPlayerRef={(p) => { playerRef.current = p; }}
      />

      <div className="video-player-controls">
        <Tooltip label="Rewind 10 seconds">
          <button className="icon-btn icon-btn--labeled" onClick={() => seekBy(-10)} aria-label="Rewind 10 seconds">
            <Icon name="rewind" size={16} /><span aria-hidden="true">10s</span>
          </button>
        </Tooltip>
        <Tooltip label={playing ? 'Pause' : 'Play'}>
          <button className="icon-btn icon-btn--primary" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            <Icon name={playing ? 'pause' : 'play'} />
          </button>
        </Tooltip>
        <Tooltip label="Forward 10 seconds">
          <button className="icon-btn icon-btn--labeled" onClick={() => seekBy(10)} aria-label="Forward 10 seconds">
            <span aria-hidden="true">10s</span><Icon name="forward" size={16} />
          </button>
        </Tooltip>
        {/* Less used: the playlist's previous / next video, behind "…" */}
        {isPlaylist && (
          <Tooltip label="More options">
            <button className={`icon-btn ${showMore ? 'icon-btn--on' : ''}`} onClick={() => setShowMore(!showMore)}
              aria-label="More options" aria-expanded={showMore}>
              <Icon name="more" />
            </button>
          </Tooltip>
        )}
        <span className="video-player-time">{formatTime(currentTime)}</span>
      </div>
      {isPlaylist && showMore && (
        <div className="video-player-more" role="group" aria-label="More options">
          <button className="video-player-more-btn" onClick={() => playlistStep('previousVideo')} aria-label="Previous video">
            <Icon name="skip-back" size={16} /> Previous video
          </button>
          <button className="video-player-more-btn" onClick={() => playlistStep('nextVideo')} aria-label="Next video">
            Next video <Icon name="skip-forward" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default MediaPlayer;
